import React, { useRef, useEffect, useCallback } from 'react'
import * as d3 from 'd3'
import { BiMensalStore, WorkdayPoint, Metric, Mode, MAIN_COLOR, COMPARING_COLOR } from './BiEvolucaoMensal'
import { buildPiecewiseTrendMap } from './projecao-mensal'

const MARGIN = { top: 20, right: 20, bottom: 40, left: 60 }
const HEIGHT = 640
const TREND_COLOR = '#ec4899'

export function MensalChart() {
  const { monthPoints, comparingMonthPoints, metric, month, comparingMonth, mode } = BiMensalStore.useState()
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const monthPointsRef = useRef(monthPoints)
  const comparingMonthPointsRef = useRef(comparingMonthPoints)
  const metricRef = useRef(metric)
  const monthRef = useRef(month)
  const comparingMonthRef = useRef(comparingMonth)
  const modeRef = useRef(mode)

  monthPointsRef.current = monthPoints
  comparingMonthPointsRef.current = comparingMonthPoints
  metricRef.current = metric
  monthRef.current = month
  comparingMonthRef.current = comparingMonth
  modeRef.current = mode

  const draw = useCallback(() => {
    const monthPoints = monthPointsRef.current
    const comparingMonthPoints = comparingMonthPointsRef.current
    const metric = metricRef.current
    const month = monthRef.current
    const comparingMonth = comparingMonthRef.current
    const mode = modeRef.current

    if (!svgRef.current || !containerRef.current) return

    const todayStr = new Date().toLocaleDateString('en-CA')
    const currentMonthStr = todayStr.slice(0, 7)
    const isCurrentMonth = month === currentMonthStr

    const pastMonthPoints = isCurrentMonth ? monthPoints.filter((p) => p.date < todayStr) : []
    const lastPastPoint = pastMonthPoints[pastMonthPoints.length - 1]

    const todayWorkdayPoint = isCurrentMonth ? monthPoints.find((p) => p.date === todayStr) : undefined
    const firstFutureDayIndex =
      todayWorkdayPoint?.workdayIndex ?? monthPoints.find((p) => p.date > todayStr)?.workdayIndex ?? null

    // Workdays: linear trend from slope-per-workday
    const linearTrend =
      mode === 'workdays' && isCurrentMonth && lastPastPoint && lastPastPoint.workdayIndex > 0
        ? { slope: lastPastPoint[metric] / lastPastPoint.workdayIndex, lineStartIndex: lastPastPoint.workdayIndex }
        : null

    // All-days: piecewise trend using separate workday/non-workday slopes
    const piecewiseTrendMap =
      mode === 'all_days' && isCurrentMonth && lastPastPoint
        ? buildPiecewiseTrendMap(lastPastPoint, monthPoints.filter((p) => p.date >= todayStr), pastMonthPoints, metric)
        : null

    const drawableMonthPoints = isCurrentMonth
      ? monthPoints.filter((p) => p.date <= todayStr)
      : monthPoints

    svgRef.current.setAttribute('width', '0')
    const width = containerRef.current.clientWidth
    const innerWidth = width - MARGIN.left - MARGIN.right
    const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', width).attr('height', HEIGHT)

    const allPoints = [...monthPoints, ...comparingMonthPoints]
    if (allPoints.length === 0) return

    const maxDayIndex = Math.max(
      monthPoints.length > 0 ? monthPoints[monthPoints.length - 1].workdayIndex : 0,
      comparingMonthPoints.length > 0 ? comparingMonthPoints[comparingMonthPoints.length - 1].workdayIndex : 0
    )
    const rawMaxValue = Math.max(...allPoints.map((p) => p[metric]), 0)
    const trendMax =
      linearTrend !== null
        ? linearTrend.slope * maxDayIndex
        : piecewiseTrendMap !== null
          ? Math.max(...Array.from(piecewiseTrendMap.values()))
          : 0
    const maxValue = Math.max(rawMaxValue, trendMax)

    const xScale = d3.scaleLinear().domain([1, maxDayIndex]).range([0, innerWidth])
    const yScale = d3.scaleLinear().domain([0, maxValue * 1.1]).range([innerHeight, 0])

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`)

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(Math.min(maxDayIndex, 12))
          .tickFormat((d) => `D${d}`)
      )
      .attr('font-size', 11)

    g.append('g')
      .call(
        d3
          .axisLeft(yScale)
          .ticks(5)
          .tickFormat((v) => formatTickValue(Number(v), metric))
      )
      .attr('font-size', 11)

    const lineGenerator = d3
      .line<WorkdayPoint>()
      .x((p) => xScale(p.workdayIndex))
      .y((p) => yScale(p[metric]))
      .curve(d3.curveLinear)

    if (comparingMonthPoints.length > 0) {
      g.append('path')
        .datum(comparingMonthPoints)
        .attr('fill', 'none')
        .attr('stroke', COMPARING_COLOR)
        .attr('stroke-width', 2)
        .attr('d', lineGenerator)
    }

    if (drawableMonthPoints.length > 0) {
      g.append('path')
        .datum(drawableMonthPoints)
        .attr('fill', 'none')
        .attr('stroke', MAIN_COLOR)
        .attr('stroke-width', 2.5)
        .attr('d', lineGenerator)
    }

    if (linearTrend !== null && lastPastPoint !== undefined) {
      g.append('line')
        .attr('x1', xScale(linearTrend.lineStartIndex))
        .attr('x2', xScale(maxDayIndex))
        .attr('y1', yScale(lastPastPoint[metric]))
        .attr('y2', yScale(linearTrend.slope * maxDayIndex))
        .attr('stroke', TREND_COLOR)
        .attr('stroke-width', 1.5)
        .attr('opacity', 0.5)
    }

    if (piecewiseTrendMap !== null) {
      const trendPoints = Array.from(piecewiseTrendMap.entries())
        .sort(([a], [b]) => a - b)
        .map(([index, value]) => ({ workdayIndex: index, value }))
      const trendLineGenerator = d3
        .line<{ workdayIndex: number; value: number }>()
        .x((p) => xScale(p.workdayIndex))
        .y((p) => yScale(p.value))
      g.append('path')
        .datum(trendPoints)
        .attr('fill', 'none')
        .attr('stroke', TREND_COLOR)
        .attr('stroke-width', 1.5)
        .attr('opacity', 0.5)
        .attr('d', trendLineGenerator)
    }

    const verticalLine = g
      .append('line')
      .attr('stroke', '#bbb')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3')
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .style('display', 'none')

    const dotMain = g
      .append('circle')
      .attr('r', 5)
      .attr('fill', MAIN_COLOR)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('display', 'none')

    const dotComparing = g
      .append('circle')
      .attr('r', 5)
      .attr('fill', COMPARING_COLOR)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('display', 'none')

    const dotTrend = g
      .append('circle')
      .attr('r', 5)
      .attr('fill', TREND_COLOR)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .attr('opacity', 0.7)
      .style('display', 'none')

    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .on('mousemove', function (event) {
        const [mx] = d3.pointer(event)
        const clamped = Math.max(1, Math.min(Math.round(xScale.invert(mx)), maxDayIndex))
        const x = xScale(clamped)

        const mainPoint = monthPoints.find((p) => p.workdayIndex === clamped)
        const comparingPoint = comparingMonthPoints.find((p) => p.workdayIndex === clamped)
        const isFuture = mainPoint ? mainPoint.date > todayStr : false
        const isToday = mainPoint ? mainPoint.date === todayStr : false

        verticalLine.attr('x1', x).attr('x2', x).style('display', null)

        if (mainPoint && !isFuture) {
          dotMain.attr('cx', x).attr('cy', yScale(mainPoint[metric])).style('display', null)
        } else {
          dotMain.style('display', 'none')
        }

        if (comparingPoint) {
          dotComparing.attr('cx', x).attr('cy', yScale(comparingPoint[metric])).style('display', null)
        } else {
          dotComparing.style('display', 'none')
        }

        let projectedTrendValue: number | null = null
        if (mode === 'workdays' && linearTrend !== null && firstFutureDayIndex !== null && clamped >= firstFutureDayIndex) {
          projectedTrendValue = linearTrend.slope * clamped
        } else if (mode === 'all_days' && piecewiseTrendMap !== null) {
          projectedTrendValue = piecewiseTrendMap.get(clamped) ?? null
        }

        if (projectedTrendValue !== null && (isToday || isFuture)) {
          dotTrend.attr('cx', x).attr('cy', yScale(projectedTrendValue)).style('display', null)
        } else {
          dotTrend.style('display', 'none')
        }

        if (tooltipRef.current) {
          const isRightHalf = mx > innerWidth / 2
          tooltipRef.current.style.display = 'block'
          tooltipRef.current.style.left = isRightHalf
            ? `${x + MARGIN.left - 216}px`
            : `${x + MARGIN.left + 16}px`
          tooltipRef.current.style.top = `${MARGIN.top}px`
          const prevMainPoint = monthPoints.find((p) => p.workdayIndex === clamped - 1)
          const prevComparingPoint = comparingMonthPoints.find((p) => p.workdayIndex === clamped - 1)
          tooltipRef.current.innerHTML = buildTooltipHTML(
            clamped,
            mainPoint,
            comparingPoint,
            prevMainPoint,
            prevComparingPoint,
            metric,
            month,
            comparingMonth,
            mode,
            projectedTrendValue,
            isToday,
            isFuture
          )
        }
      })
      .on('mouseleave', function () {
        verticalLine.style('display', 'none')
        dotMain.style('display', 'none')
        dotComparing.style('display', 'none')
        dotTrend.style('display', 'none')
        if (tooltipRef.current) tooltipRef.current.style.display = 'none'
      })
  }, [])

  useEffect(() => {
    draw()
  }, [monthPoints, comparingMonthPoints, metric, month, comparingMonth, mode, draw])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>
    function handleResize() {
      clearTimeout(timeout)
      timeout = setTimeout(draw, 150)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', handleResize)
    }
  }, [draw])

  return (
    <div ref={containerRef} style={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
      <svg ref={svgRef} style={{ display: 'block' }} />
      <div
        ref={tooltipRef}
        style={{
          position: 'absolute',
          display: 'none',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          padding: '8px 12px',
          fontSize: 12,
          pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          minWidth: 200,
          zIndex: 10,
        }}
      />
    </div>
  )
}

function buildTooltipHTML(
  dayIndex: number,
  mainPoint: WorkdayPoint | undefined,
  comparingPoint: WorkdayPoint | undefined,
  prevMainPoint: WorkdayPoint | undefined,
  prevComparingPoint: WorkdayPoint | undefined,
  metric: Metric,
  month: string,
  comparingMonth: string,
  mode: Mode,
  projectedTrendValue: number | null,
  isToday: boolean,
  isFuture: boolean
): string {
  const dot = (color: string) =>
    `<span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block;flex-shrink:0"></span>`

  const row = (color: string, label: string, point: WorkdayPoint, prevPoint: WorkdayPoint | undefined) =>
    `<div style="display:flex;align-items:center;gap:6px;margin-top:4px">` +
    `${dot(color)}` +
    `<span style="color:#666;font-size:11px">${label}</span>` +
    `<span style="margin-left:auto;font-weight:500">${formatValue(point[metric], metric)}</span>` +
    `</div>` +
    `<div style="display:flex;align-items:center;padding-left:14px;color:#999;font-size:11px">` +
    `<span>${formatDate(point.date)}</span>` +
    (prevPoint !== undefined ? formatDelta(point[metric] - prevPoint[metric], metric) : '') +
    `</div>`

  const projectedRow = (label: string, value: number, date: string) =>
    `<div style="display:flex;align-items:center;gap:6px;margin-top:4px">` +
    `${dot(TREND_COLOR)}` +
    `<span style="color:#666;font-size:11px">${label}</span>` +
    `<span style="margin-left:auto;font-weight:500">${formatValue(value, metric)}</span>` +
    `</div>` +
    `<div style="padding-left:14px;color:#999;font-size:11px">${formatDate(date)}</div>`

  const dayLabel = mode === 'workdays' ? `Dia útil ${dayIndex}` : `Dia ${dayIndex}`

  return (
    `<div style="font-weight:600;margin-bottom:2px">${dayLabel}</div>` +
    (mainPoint && !isFuture ? row(MAIN_COLOR, formatMonth(month), mainPoint, prevMainPoint) : '') +
    (projectedTrendValue !== null && (isToday || isFuture) && mainPoint
      ? projectedRow('Tendência', projectedTrendValue, mainPoint.date)
      : '') +
    (comparingPoint ? row(COMPARING_COLOR, formatMonth(comparingMonth), comparingPoint, prevComparingPoint) : '')
  )
}

function formatMonth(month: string): string {
  const [year, m] = month.split('-')
  const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  return `${MONTHS[parseInt(m) - 1]} ${year}`
}

function formatDate(date: string): string {
  const d = new Date(date + 'T00:00:00')
  const weekday = d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')
  const dayMonth = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  return `${weekday} ${dayMonth}`
}

function formatTickValue(value: number, metric: string): string {
  if (metric === 'valorNewDriverSum') {
    return value >= 1000 ? `${(value / 1000).toFixed(0)}k` : String(value)
  }
  return String(value)
}

function formatValue(value: number, metric: string): string {
  if (metric === 'valorNewDriverSum') {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0,
    }).format(value)
  }
  return String(value)
}

function formatDelta(delta: number, metric: string): string {
  if (delta === 0) return ''
  const arrow = delta > 0 ? '▲' : '▼'
  const color = delta > 0 ? '#22c55e' : '#ef4444'
  return `<span style="margin-left:auto;color:${color}">${arrow} ${formatValue(Math.abs(delta), metric)}</span>`
}
